import { create } from 'zustand';
import { ECoinflipSide, ICoinflipGame, ICoinflipHistoryGame } from '@/utils/types/ICoinflipGame';
import { ApiRequest } from '@/utils/axios';

// Marks coinflip actions the *current tab* initiated. The coinflipCreated /
// coinflipJoined socket broadcasts reach every tab the user has open, so
// without this guard each tab would auto-open (and switch) its "View coinflip"
// modal to a game created or joined on another tab. A tab only auto-opens the
// games it itself initiated through these markers. Non-reactive on purpose —
// it's a cross-tab coordination flag, not render state.
export const coinflipIntent = {
    create: false, // this tab just fired a create and expects to open its game
    joinIds: new Set<number>(), // game ids this tab just joined
};

function adaptHistoryGame(game: ICoinflipHistoryGame): ICoinflipGame {
    const sortedUsers =
        game.winnerSide === 1
            ? [game.winnerUser, game.loserUser]
            : [game.loserUser, game.winnerUser];

    const firstUser = sortedUsers[0];
    const secondUser = sortedUsers[1];

    return {
        id: game.id,
        firstUser,
        firstSide: 1,
        firstItems: game.skins[firstUser.id],
        secondUser,
        secondSide: 2,
        secondItems: game.skins[secondUser.id],
        winnerSide: game.winnerSide,
        endsAt: null,
        flipStartAt: null,
        revealAt: null,
        clearAt: null,
        createdAt: game.createdAt,
        hash: game.hash,
        secret: game.secret,
    } as ICoinflipGame;
}

interface coinflipState {
    minbet: number;
    games: Map<number, ICoinflipGame>;
    historyGameIds: Set<number>;
    activeGameIds: Set<number>;
    // Finished games that shouldn't appear in History yet (still playing their
    // on-main reveal animation). They're released via addToHistory.
    pendingFinishIds: Set<number>;
    // False until the initial history fetch resolves (drives the loading state).
    historyLoaded: boolean;
    historyGames: () => ICoinflipGame[];
    activeGames: () => ICoinflipGame[];
    loadActiveGames: () => Promise<void>;
    loadHistoryGames: () => Promise<void>;
    finalizeGame: (gameId: number) => Promise<void>;
    updateGame: (game: ICoinflipGame) => void;
    releaseReservation: (gameId: number) => void;
    deleteGame: (gameId: number) => void;
    setWinnerByGameId: (gameId: number, winnerSide: ECoinflipSide) => void;
    applyFinish: (
        gameId: number,
        payload: {
            winnerSide: ECoinflipSide;
            flipStartAt: number;
            revealAt: number;
            clearAt: number;
            // Provably-fair fields carried by the socket finish event so the
            // client never has to re-fetch the history list to fill them in.
            hash?: string;
            secret?: string;
        }
    ) => void;
    moveToHistory: (gameId: number) => void;
    addToHistory: (gameId: number) => void;
    markPending: (gameId: number) => void;
    clear: () => void;
}

export const coinflipStore = create<coinflipState>((set, get) => ({
    minbet: 10,
    games: new Map<number, ICoinflipGame>(),
    historyGameIds: new Set<number>(),
    activeGameIds: new Set<number>(),
    pendingFinishIds: new Set<number>(),
    historyLoaded: false,
    historyGames() {
        return Array.from(get().historyGameIds)
            .sort((a, b) => b - a)
            .map((id) => get().games.get(id)!);
    },
    activeGames() {
        return Array.from(get().activeGameIds).map((id) => get().games.get(id)!);
    },
    loadActiveGames: async () => {
        const {
            data: {
                response: { games, minbet },
            },
        } = await ApiRequest.get('/coinflip');

        set((state) => {
            const newGames = new Map(state.games);
            const newActiveGameIds = new Set<number>();

            for (const oldActiveId of state.activeGameIds) {
                if (!state.historyGameIds.has(oldActiveId)) newGames.delete(oldActiveId);
            }

            games.forEach((game: ICoinflipGame) => {
                newGames.set(game.id, game);
                newActiveGameIds.add(game.id);
            });

            return {
                games: newGames,
                minbet: parseFloat(minbet),
                activeGameIds: newActiveGameIds,
            };
        });
    },
    loadHistoryGames: async () => {
        const {
            data: {
                response: { history },
            },
        } = await ApiRequest.get<{ response: { history: ICoinflipHistoryGame[] } }>(
            '/coinflip/history/all'
        );

        set((state) => {
            const games = new Map(state.games);
            const historyGameIds = new Set(state.historyGameIds);

            for (const game of history) {
                // Skip games still playing their on-main reveal — don't surface
                // them in History or clobber their live data until released.
                if (state.pendingFinishIds.has(game.id)) continue;
                games.set(game.id, adaptHistoryGame(game));
                historyGameIds.add(game.id);
            }

            return { games, historyGameIds, historyLoaded: true };
        });
    },
    finalizeGame: async (gameId: number) => {
        const {
            data: {
                response: { history },
            },
        } = await ApiRequest.get<{ response: { history: ICoinflipHistoryGame[] } }>(
            '/coinflip/history/all'
        );

        set((state) => {
            const games = new Map(state.games);
            const historyGameIds = new Set(state.historyGameIds);
            const activeGameIds = new Set(state.activeGameIds);

            for (const game of history) {
                games.set(game.id, adaptHistoryGame(game));
                historyGameIds.add(game.id);
            }
            activeGameIds.delete(gameId);

            return { games, historyGameIds, activeGameIds };
        });
    },
    updateGame(game: ICoinflipGame) {
        const alreadyInList = get().games.has(game.id);
        set((state) => {
            const games = new Map(state.games);
            const activeGameIds = new Set(state.activeGameIds);

            games.set(game.id, game);

            if (!alreadyInList) {
                activeGameIds.add(game.id);
            }

            return { games, activeGameIds };
        });
    },
    releaseReservation(gameId: number) {
        set((state) => {
            const game = state.games.get(gameId);
            if (!game) return state;
            const games = new Map(state.games);
            games.set(gameId, { ...game, pendingJoin: null });
            return { games };
        });
    },
    deleteGame(gameId: number) {
        set((state) => {
            const games = new Map(state.games);
            const activeGameIds = new Set(state.activeGameIds);
            const historyGameIds = new Set(state.historyGameIds);

            activeGameIds.delete(gameId);
            historyGameIds.delete(gameId);
            games.delete(gameId);

            return { games, activeGameIds, historyGameIds };
        });
    },
    setWinnerByGameId: (gameId, winnerSide) =>
        set((state) => {
            const games = new Map(state.games);
            if (games.has(gameId)) {
                const game = games.get(gameId)!;
                game.winnerSide = winnerSide;
                games.set(gameId, game);
            }
            return { games };
        }),
    applyFinish: (gameId, { winnerSide, flipStartAt, revealAt, clearAt, hash, secret }) =>
        set((state) => {
            const games = new Map(state.games);
            const game = games.get(gameId);
            if (!game) return state;
            games.set(gameId, {
                ...game,
                winnerSide,
                flipStartAt,
                revealAt,
                clearAt,
                // Only overwrite when the event actually carried them, so a
                // partial payload never wipes existing values.
                ...(hash !== undefined ? { hash } : {}),
                ...(secret !== undefined ? { secret } : {}),
            });
            return { games };
        }),
    moveToHistory: (gameId) =>
        set((state) => {
            if (!state.games.has(gameId)) return state;
            const activeGameIds = new Set(state.activeGameIds);
            const historyGameIds = new Set(state.historyGameIds);
            const pendingFinishIds = new Set(state.pendingFinishIds);
            activeGameIds.delete(gameId);
            pendingFinishIds.delete(gameId);
            historyGameIds.add(gameId);
            return { activeGameIds, historyGameIds, pendingFinishIds };
        }),
    // Surface a finished game in History WITHOUT removing it from Active yet —
    // it stays on the main list (playing its win animation) for a grace window.
    // Also clears the "pending" gate so it becomes visible in History.
    addToHistory: (gameId) =>
        set((state) => {
            if (!state.games.has(gameId)) return state;
            const pendingFinishIds = new Set(state.pendingFinishIds);
            pendingFinishIds.delete(gameId);
            const historyGameIds = new Set(state.historyGameIds);
            historyGameIds.add(gameId);
            return { historyGameIds, pendingFinishIds };
        }),
    // Mark a just-finished game as pending so it does NOT show in History until
    // its on-main reveal animation has played out (released via addToHistory).
    markPending: (gameId) =>
        set((state) => {
            const pendingFinishIds = new Set(state.pendingFinishIds);
            pendingFinishIds.add(gameId);
            return { pendingFinishIds };
        }),
    clear() {
        set(() => {
            return {
                games: new Map<number, ICoinflipGame>(),
                historyGameIds: new Set<number>(),
                activeGameIds: new Set<number>(),
                pendingFinishIds: new Set<number>(),
                historyLoaded: false,
            };
        });
    },
}));
